import { SkeletonLoader } from "@via/components/loading/loading.tsx";
import { TreeStructure } from "@via/components/tree-structure/tree-structure.tsx";
import { useLatestRunLogs } from "@via/pages/projects/runs/runs.api.tsx";
import { EMPTY_TABLE_MESSAGE, ROUTES, RunTabs } from "@via/utils/constants.tsx";
import { EmptyDataIcon } from "@via/utils/custom-icons.tsx";
import TabularLogs from "@via/pages/projects/runs/run-logs/tabular-logs.tsx";
import { useAuth } from "@via/hooks/useAuth.tsx";
import { useRegisterCortexContext } from "@via/hooks/useRegisterCortexContext";
import {
  type SelectOption,
  TreeData,
  TreeDataScriptInterface,
} from "@via/utils/interfaces.tsx";
import {
  Dispatch,
  type FC,
  type ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { NodeApi } from "react-arborist";
import {
  ImperativePanelHandle,
  Panel,
  PanelGroup,
} from "react-resizable-panels";
import { useParams } from "react-router-dom";
import PanelResizer from "@via/components/panel-resizer/panel-resizer.tsx";
import { classNames } from "@via/utils/helpers.tsx";

// Defined outside component since it has no dependencies on props or state
const formatLogData = (data: any[]) => {
  // don't expose "initialrun/nextflow.nf"
  const order = [
    "Log.txt",
    "trace.txt",
    ".nextflow.log",
    "nextflow.nf",
    "nextflow.config",
    "timeline.html",
    "report.html",
    "initialrun/trace.txt",
    "initialrun/.nextflow.log",
    "initialrun/nextflow.nf",
    "initialrun/nextflow.config",
    ".command.log",
    ".command.out",
    ".command.err",
  ];
  // In the future, we plan to have "Nextflow Config" folder
  const serverLogs = data
    .sort((a, b) => {
      return order.indexOf(a.name) - order.indexOf(b.name);
    })
    .map((item, index) => {
      return { id: `${index + 1}`, name: item.name };
    });
  return [{ id: "0", name: "Server Logs", children: serverLogs }];
};

interface RunLogsProps {
  uuid: string;
  shouldRefetchLogs: boolean;
  runLogsContent: ReactNode | string | null;
  setRunLogsContent: Dispatch<SetStateAction<ReactNode | string | null>>;
  selectedRunAttempt?: SelectOption;
  activeRunTab?: RunTabs;
  /**
   * A run launch was requested and has not completed yet. Until it does, the logs
   * endpoint still returns the previous attempt, so show the loading state instead
   * of publishing that stale output.
   */
  isAwaitingNewRunLogs?: boolean;
}

const RunLogs: FC<RunLogsProps> = ({
  uuid,
  shouldRefetchLogs,
  runLogsContent,
  setRunLogsContent,
  selectedRunAttempt,
  activeRunTab,
  isAwaitingNewRunLogs = false,
}) => {
  const { runId = "" } = useParams();
  const { config } = useAuth();
  const {
    data,
    isLoading,
    isError,
    refetch: refetchRunLogs,
  } = useLatestRunLogs(runId, shouldRefetchLogs, selectedRunAttempt?.value);
  const [selected, setSelectedItem] = useState<string | null>(null);
  const [treeData, setTreeData] = useState<TreeDataScriptInterface[]>([]);

  const [isPanelCollapsed, setPanelCollapsed] = useState(false);
  const panelRef = useRef<ImperativePanelHandle | null>(null);

  const [autoScroll, setAutoScroll] = useState(true);
  const bottomContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  // Memoize getContext to prevent infinite re-renders
  const getLogsContext = useCallback(
    () => ({
      runId,
      uuid,
      // Per-file context: treat the selected log file as the file to embed/search.
      // This leverages the existing file-based RAG pipeline (same as reports).
      filePath: selected || "",
      runUUID: uuid,
      fileSource: "logs",
    }),
    [runId, uuid, selected]
  );

  // Register cortex context handler for this page
  useRegisterCortexContext({
    routePattern: ROUTES.RUN_DETAILS,
    // Include the selected file name so the drawer can detect context changes and
    // suggest creating a new conversation when the user switches log files.
    contextName: selected ? `Run Log: ${selected}` : "Run Logs",
    getContext: getLogsContext,
    systemPrompt:
      "You are analyzing a pipeline run log file. Help identify errors, warnings, and provide insights based on the log content provided.",
    enabled: activeRunTab === RunTabs.Logs && !!selected,
  });

  useEffect(() => {
    if (!autoScroll || selected?.toLowerCase() !== "log.txt") return;

    let observer: ResizeObserver | null = null;
    let interval: NodeJS.Timeout | undefined = undefined;

    const attachObserver = (element: HTMLElement) => {
      observer = new ResizeObserver(entries => {
        for (const entry of entries) {
          const rect = entry.target.getBoundingClientRect();
          if (rect.height > 0 || rect.width > 0) {
            element.scrollIntoView({ behavior: "smooth" });
            observer?.disconnect();
          }
        }
      });
      observer.observe(element);
    };

    // Wait until ref is attached
    interval = setInterval(() => {
      if (bottomContainerRef.current) {
        clearInterval(interval);
        attachObserver(bottomContainerRef.current);
      }
    }, 50);

    return () => {
      if (interval) clearInterval(interval);
      if (observer) observer.disconnect();
    };
  }, [runLogsContent, selected, autoScroll]);

  // Reset auto-scroll when switching to log.txt
  useEffect(() => {
    if (selected?.toLowerCase() === "log.txt") {
      setAutoScroll(true);
    }
  }, [selected]);

  useEffect(() => {
    if (selectedRunAttempt) {
      refetchRunLogs();
    }
  }, [selectedRunAttempt, refetchRunLogs]);

  const contentHandler = useCallback(
    (name: string | undefined) => {
      if (!name || !data) {
        setRunLogsContent("No text to display at this time");
        return;
      }
      const match = data.find(log => log.name === name);
      if (match) {
        const fileExtension = match.name.split(".").pop();
        if (fileExtension === "html" && match.content) {
          // Set the HTML content directly using srcdoc in iframe
          setRunLogsContent(
            <iframe srcDoc={match.content} className="w-full h-full" />
          );
        } else if (match.name.endsWith("trace.txt")) {
          // For trace.txt files, display the content as a table
          setRunLogsContent(<TabularLogs logs={match.content} />);
        } else if (match.content) {
          // For other file types, display the content as text
          setRunLogsContent(match.content);
        } else {
          setRunLogsContent("No text to display at this time");
        }
      } else {
        setRunLogsContent("No text to display at this time");
      }
    },
    [data, setRunLogsContent]
  );

  useEffect((): void => {
    // While a launch is pending, `data` is still the previous attempt's logs.
    if (isAwaitingNewRunLogs) return;
    if (data) {
      const formatted = formatLogData(data);
      setTreeData(formatted);
      if (selected) {
        contentHandler(selected);
      } else {
        const firstLog = formatted[0]?.children[0]?.name;
        setSelectedItem(firstLog);
        contentHandler(firstLog);
      }
    }
  }, [data, contentHandler, isAwaitingNewRunLogs]);

  // Refresh content when parent clears runLogsContent (e.g., on re-run)
  // This only responds to runLogsContent being set to null, not to data/selected changes
  // (which are already handled by the effect above)
  useEffect(() => {
    // Without this guard the clear performed on re-run is undone on the very next
    // commit: this effect would re-publish the old attempt's logs straight back
    // out of the still-cached `data`, which is what made them flash back.
    if (isAwaitingNewRunLogs) return;
    if (runLogsContent === null && selected) {
      contentHandler(selected);
    }
  }, [runLogsContent, selected, contentHandler, isAwaitingNewRunLogs]);

  const onItemSelect = (node: Array<NodeApi<TreeData>>) => {
    if (node[0]?.data) {
      const selected = node[0]?.data || null;
      setSelectedItem(selected?.name);
      contentHandler(selected?.name);
    }
  };

  return (
    <main className="pt-4">
      {isLoading || isAwaitingNewRunLogs ? (
        <div className="grid grid-cols-1 gap-6 py-6 sm:grid-cols-2 lg:grid-cols-3">
          <SkeletonLoader count={12} />
        </div>
      ) : isError || !treeData.length ? (
        <div className="via-generic-error">
          <EmptyDataIcon />
          <p>{EMPTY_TABLE_MESSAGE.LOGS}</p>
        </div>
      ) : (
        <div className="flex flex-col">
          <div className="flex flex-row overflow-x-scroll overflow-y-scroll">
            <PanelGroup direction="horizontal">
              <Panel
                ref={panelRef}
                collapsible={true}
                onCollapse={() => setPanelCollapsed(true)}
                onExpand={() => setPanelCollapsed(false)}
                defaultSize={20}
                maxSize={100}>
                <div className="h-[65dvh] overflow-y-scroll overflow-x-hidden">
                  <TreeStructure
                    selection="1"
                    openByDefault
                    disableFolderSelection
                    treeData={treeData}
                    onSelect={onItemSelect}
                  />
                </div>
              </Panel>
              <PanelResizer
                panelRef={panelRef}
                isPanelCollapsed={isPanelCollapsed}
              />
              <Panel minSize={30}>
                <div
                  ref={scrollContainerRef}
                  className="h-[65dvh] w-full overflow-y-scroll text-xs">
                  {/* Render preformatted text or iframe based on content */}
                  {typeof runLogsContent === "string" ? (
                    <pre
                      className={classNames(
                        selected?.toLowerCase()?.includes("log")
                          ? "whitespace-pre-wrap break-words"
                          : ""
                      )}
                      onClick={() => {
                        if (selected?.toLowerCase() === "log.txt") {
                          setAutoScroll(false);
                        }
                      }}>
                      {runLogsContent}
                    </pre>
                  ) : (
                    runLogsContent
                  )}
                  <div ref={bottomContainerRef}></div>
                </div>
              </Panel>
            </PanelGroup>
          </div>
          <div className="ml-auto flex items-center gap-4 text-xs text-gray-500">
            {/* Only when the overlay is deployed. An empty GRAFANA_BASE_URL is the "not installed"
              signal, the same way FOUNDRY_CLASSIC_BUTTON_URL gates its button in the header. */}
            {config?.GRAFANA_BASE_URL && (
              <a
                href={`${config.GRAFANA_BASE_URL}/d/foundry-run-lifecycle/run-lifecycle?var-run_id=${encodeURIComponent(
                  runId
                )}&from=now-7d&to=now`}
                target="_blank"
                rel="noreferrer"
                className="text-indigo-600 hover:text-indigo-800"
                data-testid="viewRunInGrafana">
                View in Grafana
              </a>
            )}
            <span>Run UUID: {uuid}</span>
          </div>
        </div>
      )}
    </main>
  );
};

export default RunLogs;
